import { useEffect, useRef } from 'react';
import { milliseconds } from 'date-fns';
import { useLocation, useSubmit } from 'react-router';

import { flaskApiHeartbeat } from '~/auth/flask-api';
import { paths } from '~/constants/paths';

import { useRootLoader } from './useUser';

const INACTIVITY_TIMEOUT_MS = milliseconds({ minutes: 30 });
const HEARTBEAT_INTERVAL_MS = milliseconds({ minutes: 10 });
const INTERVAL_MS = milliseconds({ seconds: 1 });

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

export function useDetectInactivity() {
  const interval = useRef<NodeJS.Timeout | undefined>(undefined);
  const msElapsed = useRef(0);
  const lastHeartbeat = useRef(Date.now());
  const submit = useSubmit();
  const location = useLocation();
  const { user } = useRootLoader();

  // reset to 0 on user interaction
  useEffect(() => {
    const resetTimer = () => {
      msElapsed.current = 0;

      // Heartbeat: refresh tokens every 10 min when user is active
      // This keeps Auth0's idle session timer from expiring
      // Only send heartbeat if user is authenticated
      const now = Date.now();
      if (user && now - lastHeartbeat.current > HEARTBEAT_INTERVAL_MS) {
        lastHeartbeat.current = now;
        flaskApiHeartbeat().catch(() => {
          // Silently ignore - the next API call will handle auth failures
        });
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });

      clearInterval(interval.current);
      msElapsed.current = 0;
    };
  }, [user]);

  // start counter of inactivity
  useEffect(() => {
    interval.current = setInterval(() => {
      if (user && msElapsed.current >= INACTIVITY_TIMEOUT_MS) {
        clearInterval(interval.current);
        msElapsed.current = 0;
        const formData = new FormData();

        submit(formData, {
          action: paths.logout,
          method: 'post',
        });
      }

      msElapsed.current += INTERVAL_MS;
    }, INTERVAL_MS);
  }, [user, submit]);

  // detect path change and reset counter
  useEffect(() => {
    msElapsed.current = 0;
  }, [location.pathname]);

  // clear interval when user logs out
  useEffect(() => {
    if (!user && interval.current) {
      clearInterval(interval.current);
    }
  }, [user]);

  return null;
}
